import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, Edit2, Trash2, Tag as TagIcon } from "lucide-react";
import { api } from "@/api/client";
import ConfirmModal from "@/components/common/ConfirmModal";
import { useConfirmModal } from "@/hooks/useConfirmModal";

interface Tag {
  id: string;
  nama: string;
  kategori?: string;
  warna?: string;
  is_active?: boolean;
}

export default function TagListPage() {
  const [showForm, setShowForm] = useState(false);
  const [editItem, setEditItem] = useState<Tag | null>(null);
  const [formData, setFormData] = useState({ nama: "", kategori: "", warna: "#3B82F6" });
  const queryClient = useQueryClient();
  const { modalProps, confirm } = useConfirmModal();

  const { data: tags = [], isLoading } = useQuery<Tag[]>({
    queryKey: ["tags"],
    queryFn: () => api.get("/tags/"),
  });

  const createMutation = useMutation({
    mutationFn: (data: typeof formData) => api.post("/tags/", data),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ["tags"] }); resetForm(); },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: typeof formData }) => api.put(`/tags/${id}`, data),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ["tags"] }); resetForm(); },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/tags/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["tags"] }),
  });

  const resetForm = () => {
    setShowForm(false);
    setEditItem(null);
    setFormData({ nama: "", kategori: "", warna: "#3B82F6" });
  };

  const handleEdit = (tag: Tag) => {
    setEditItem(tag);
    setFormData({ nama: tag.nama, kategori: tag.kategori || "", warna: tag.warna || "#3B82F6" });
    setShowForm(true);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (editItem) {
      updateMutation.mutate({ id: editItem.id, data: formData });
    } else {
      createMutation.mutate(formData);
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Manajemen Tag</h1>
          <p className="text-sm text-gray-500 mt-0.5">{tags.length} tag terdaftar</p>
        </div>
        <button
          onClick={() => { resetForm(); setShowForm(true); }}
          className="flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
        >
          <Plus className="h-4 w-4" />
          Tambah Tag
        </button>
      </div>

      {/* Form */}
      {showForm && (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
          <h3 className="text-sm font-semibold text-gray-800 mb-4">
            {editItem ? "Edit" : "Tambah"} Tag
          </h3>
          <form onSubmit={handleSubmit} className="grid grid-cols-1 gap-4 sm:grid-cols-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Nama</label>
              <input
                value={formData.nama}
                onChange={(e) => setFormData({ ...formData, nama: e.target.value })}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                placeholder="Nama tag..."
                required
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Kategori</label>
              <input
                value={formData.kategori}
                onChange={(e) => setFormData({ ...formData, kategori: e.target.value })}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                placeholder="Contoh: tahun, prioritas..."
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Warna</label>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={formData.warna}
                  onChange={(e) => setFormData({ ...formData, warna: e.target.value })}
                  className="h-9 w-12 rounded border border-gray-300 cursor-pointer"
                />
                <span className="text-xs text-gray-500 font-mono">{formData.warna}</span>
              </div>
            </div>
            <div className="flex items-end gap-2">
              <button
                type="submit"
                disabled={createMutation.isPending || updateMutation.isPending}
                className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
              >
                {editItem ? "Simpan" : "Tambah"}
              </button>
              <button type="button" onClick={resetForm}
                className="rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50">
                Batal
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Tag list */}
      {isLoading ? (
        <div className="flex items-center justify-center h-48">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {tags.map((tag) => (
            <div key={tag.id} className="flex items-center justify-between rounded-xl border border-gray-200 bg-white px-4 py-3 shadow-sm">
              <div className="flex items-center gap-3">
                <div
                  className="h-8 w-8 rounded-full flex items-center justify-center"
                  style={{ backgroundColor: tag.warna ? `${tag.warna}20` : "#f3f4f6" }}
                >
                  <TagIcon className="h-4 w-4" style={{ color: tag.warna || "#6b7280" }} />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-800">{tag.nama}</p>
                  {tag.kategori && (
                    <p className="text-xs text-gray-400">{tag.kategori}</p>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-1">
                <button onClick={() => handleEdit(tag)}
                  className="rounded p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50">
                  <Edit2 className="h-4 w-4" />
                </button>
                <button
                  onClick={() =>
                    confirm({
                      title: "Hapus Tag",
                      message: "Tag ini akan dihapus secara permanen. Lanjutkan?",
                      confirmLabel: "Hapus",
                      variant: "danger",
                      onConfirm: () => deleteMutation.mutate(tag.id),
                    })
                  }
                  className="rounded p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
          {tags.length === 0 && (
            <div className="col-span-full text-center py-12 text-gray-400 text-sm">
              Belum ada tag
            </div>
          )}
        </div>
      )}

      <ConfirmModal {...modalProps} />
    </div>
  );
}
